import type { Absence, AbsenceType } from "@/hooks/use-absences";
import { countWorkingDaysInOverlap, WORK_HOURS_PER_DAY } from "./billing-calculations";
import { parseISODate } from "./format";

/**
 * A4-smoke-fiks (2026-07-18): delt visnings-mapping og periode-filter for
 * fravær, brukt av både `CloseMonthAbsence` (Avslutt måned) og
 * `AbsenceRangeSummary` (Timer-kortet på Mine oppgaver, uke-/månedsvisning).
 * Trukket ut hit for å unngå duplikat-litteraler (CLAUDE.md regel 3) — begge
 * steder skal vise fravær identisk uansett hvor det ble registrert (løpende
 * eller ved månedsslutt).
 */
export const ABSENCE_TYPE_LABELS: Record<AbsenceType, string> = {
  sick: "Syk",
  vacation: "Ferie",
  avspasering: "Avspasering",
};

export const ABSENCE_TYPE_DOTS: Record<AbsenceType, string> = {
  sick: "bg-orange-400",
  vacation: "bg-blue-400",
  avspasering: "bg-purple-400",
};

/**
 * Fravær hvis `[start_date, end_date]` overlapper `[rangeStart, rangeEnd]`
 * (begge inklusive, "YYYY-MM-DD"). Ren streng-sammenligning — datoene er
 * allerede date-only ("YYYY-MM-DD"), som sammenlignes leksikografisk riktig
 * i ISO-format, samme mønster som `CloseMonthAbsence` brukte før uttrekket.
 */
export function filterAbsencesInRange(
  absences: Absence[],
  rangeStart: string,
  rangeEnd: string,
): Absence[] {
  return absences.filter((a) => a.start_date <= rangeEnd && a.end_date >= rangeStart);
}

/**
 * VIB-1732: omfanget av et fravær, i timer, KUN til visning.
 *
 * `hours` på raden er en markør for DELDAGSFRAVÆR, ikke «hvor mange timer
 * fraværet er». Både `use-time-gaps` og `billing-calculations` forgrener på
 * den: er den satt, teller de den ÉN gang på startdatoen og ignorerer
 * datointervallet. Lagret man derfor 7,5 på et flerdagersfravær, ville en
 * femdagers sykemelding redusert kapasiteten med 7,5t i stedet for 37,5t —
 * rett inn i faktureringsgraden.
 *
 * Derfor beregnes heldagsfravær her i stedet for å lagres: 7,5t per ukedag i
 * perioden. Helger teller ikke, samme regel som kapasitetsberegningen bruker.
 */
export function absenceDisplayHours(absence: Pick<Absence, "start_date" | "end_date" | "hours">): number {
  if (absence.hours != null) return absence.hours;
  const start = parseISODate(absence.start_date);
  const end = parseISODate(absence.end_date);
  return countWorkingDaysInOverlap(start, end, start, end) * WORK_HOURS_PER_DAY;
}
